import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Pause,
  Play,
  Volume2,
  VolumeX,
} from 'lucide-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useRouter, useSearch } from '@tanstack/react-router';
import { overlay } from 'overlay-kit';

import { courseQueries, postQuizzesAnswer } from '@/entities/course';
import { useCaptureAnalyticsEvent } from '@/shared/analytics/events';
import { Button } from '@/shared/ui/button';
import { Reel, ReelContent, ReelItem } from '@/shared/ui/kibo-ui/reel';
import { useToast } from '@/shared/ui/toast-provider';
import { cn } from '@/shared/utils';
import type { ErrorResponse } from '@/shared/types/api';
import {
  pickPreferredMediaSource,
  useQuizAudioPlayer,
} from '../model/use-quiz-audio-player';
import { DescriptionModal } from './description-modal';

const formatTimestamp = (seconds: number) => {
  if (!Number.isFinite(seconds) || seconds < 0) return '00:00';
  const wholeSeconds = Math.floor(seconds);
  const mins = Math.floor(wholeSeconds / 60)
    .toString()
    .padStart(2, '0');
  const secs = Math.floor(wholeSeconds % 60)
    .toString()
    .padStart(2, '0');
  return `${mins}:${secs}`;
};

const ANSWER_AUTO_SUBMIT_DELAY_MS =
  import.meta.env.VITE_PUBLIC_APP_ENV === 'staging' ? 10_000 : 60_000;
const AUTO_SUBMIT_DELAY_SECONDS = Math.floor(
  ANSWER_AUTO_SUBMIT_DELAY_MS / 1000,
);

const QUIZ_PLACEHOLDER_REEL_ITEM = {
  id: 'quiz-placeholder',
  type: 'image' as const,
  src: '',
  duration: 1,
  title: 'Knowledge Check',
  description:
    'Listen to the audio and select the correct answer to the question.',
};

const MissingParameters = () => {
  const router = useRouter();
  return (
    <main className="flex min-h-screen items-center justify-center bg-slate-100 px-4 text-slate-900">
      <section className="w-full max-w-md space-y-6 rounded-3xl border border-slate-200 bg-white p-8 text-center shadow-[0_24px_60px_rgba(15,23,42,0.12)]">
        <h1 className="text-2xl font-semibold">Missing Required Information</h1>
        <p className="text-sm text-slate-500">
          To proceed with this quiz, both course and content information are
          required. Please start again from the course list.
        </p>
        <Button
          className="w-full rounded-xl"
          onClick={() => router.navigate({ to: '/courses' })}
        >
          Back to Courses
        </Button>
      </section>
    </main>
  );
};

export const QuizPage = () => {
  const router = useRouter();
  const toast = useToast();
  const captureAnalyticsEvent = useCaptureAnalyticsEvent();
  const {
    courseId,
    contentId,
    quizId: searchQuizId,
  } = useSearch({
    from: '/_user/courses/quiz',
  });

  const [activeQuizId, setActiveQuizId] = useState<string | undefined>(
    searchQuizId,
  );

  const { data: quizzes, isPending: isLoadingQuizzes } = useQuery({
    ...courseQueries.quizzes(contentId),
    enabled: !!courseId,
  });

  const { data: courseDetail } = useQuery({
    ...courseQueries.detail(courseId),
    enabled: Boolean(courseId),
  });

  const quizList = quizzes ?? [];
  const currentQuiz = !activeQuizId
    ? undefined
    : quizList.find((quiz) => quiz.id === activeQuizId);

  const courseTitle = courseDetail?.title ?? 'Quiz';
  const questionTitle =
    currentQuiz?.title ?? currentQuiz?.question ?? 'Knowledge Check';
  const questionDescription =
    currentQuiz?.description ??
    'Listen to the audio and select the correct answer to the question.';
  const currentQuizId = currentQuiz?.id;

  const hasDescriptionModalRef = useRef<string | null>(null);
  const inactivityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
  }, []);

  const setActiveQuiz = useCallback(
    (nextQuizId: string) => {
      clearInactivityTimer();
      setActiveQuizId(nextQuizId);
    },
    [clearInactivityTimer],
  );

  const {
    audioRef,
    state: playerState,
    hasSource,
    play: playAudio,
    pause: pauseAudio,
    toggleMute,
    previousQuizId,
    nextQuizId,
    playNextQuiz,
    playPreviousQuiz,
  } = useQuizAudioPlayer({
    quizzes: quizList,
    activeQuizId,
    onRequestQuizChange: setActiveQuiz,
  });

  const totalQuestionCount = quizList.length;

  const currentIndex = (() => {
    if (!quizList.length) return 0;
    if (!activeQuizId) return 0;
    const index = quizList.findIndex((quiz) => quiz.id === activeQuizId);
    return index >= 0 ? index : 0;
  })();

  const currentOrder = currentIndex + 1;

  const reelData = quizList.length
    ? quizList.map((quiz) => ({
        id: quiz.id,
        type: 'image' as const,
        src: pickPreferredMediaSource(quiz.media_sources)?.url ?? '',
        duration: Math.max(quiz.duration_secs ?? 0, 1),
        title: quiz.title ?? quiz.question ?? 'Knowledge Check',
        description:
          quiz.description ??
          'Listen to the audio and select the correct answer to the question.',
      }))
    : [QUIZ_PLACEHOLDER_REEL_ITEM];

  const handleReelIndexChange = useCallback(
    (nextIndex: number) => {
      const nextQuiz = quizList[nextIndex];
      if (!nextQuiz) return;
      setActiveQuiz(nextQuiz.id);
    },
    [quizList, setActiveQuiz],
  );

  useEffect(() => clearInactivityTimer, [clearInactivityTimer]);

  useEffect(() => {
    if (!quizzes?.length) return;

    const fallbackId =
      (searchQuizId && quizzes.some((quiz) => quiz.id === searchQuizId)
        ? searchQuizId
        : quizzes[0]?.id) ?? undefined;

    setActiveQuizId((previous) =>
      previous === fallbackId ? previous : fallbackId,
    );
  }, [quizzes, searchQuizId]);

  useEffect(() => {
    if (!courseId || !contentId || !activeQuizId) return;
    if (searchQuizId === activeQuizId) return;

    router.navigate({
      to: '/courses/quiz',
      search: {
        courseId,
        contentId,
        quizId: activeQuizId,
      },
      replace: true,
    });
  }, [activeQuizId, contentId, courseId, router, searchQuizId]);

  const { mutateAsync: submitAnswer, isPending: isSubmittingAnswer } =
    useMutation({ mutationFn: postQuizzesAnswer });

  const handleContinue = useCallback(() => {
    clearInactivityTimer();

    const overlayId = hasDescriptionModalRef.current;
    if (overlayId) {
      overlay.close(overlayId);
      hasDescriptionModalRef.current = null;
    }

    if (nextQuizId) {
      playNextQuiz();
      return;
    } else {
      pauseAudio();
      router.navigate({
        to: '/courses/quiz-grading',
        search: {
          contentId,
          courseId,
        },
      });
    }
  }, [
    clearInactivityTimer,
    contentId,
    courseId,
    nextQuizId,
    pauseAudio,
    playNextQuiz,
    router,
  ]);

  const handleAnswer = useCallback(
    async (answer: boolean | null) => {
      if (!contentId || !currentQuizId || isSubmittingAnswer) return;

      try {
        pauseAudio();
        clearInactivityTimer();

        const remainingCount = Math.max(totalQuestionCount - 1, 0);
        const response = await submitAnswer({
          id: contentId,
          quiz_id: currentQuizId,
          answer_bool: answer,
        });
        captureAnalyticsEvent('quiz_answer_submitted', {
          course_id: courseId,
          content_id: contentId,
          quiz_id: currentQuizId,
          answer_value: answer === null ? 'skipped' : answer ? 'true' : 'false',
          is_correct: response.is_correct,
          attempt_type: 'initial',
          question_title: questionTitle,
          question_order:
            typeof currentOrder === 'number' ? currentOrder : undefined,
          knowledge_check_type: currentQuiz?.knowledge_check_type ?? null,
          remaining_questions: Math.max(
            totalQuestionCount - (currentIndex + 1),
            0,
          ),
        });
        hasDescriptionModalRef.current = overlay.open(({ isOpen }) => (
          <DescriptionModal
            open={isOpen}
            answer={answer}
            isCorrect={response.is_correct}
            remainingCount={remainingCount}
            questionTitle={questionTitle}
            questionDescription={questionDescription}
            explanationSource={pickPreferredMediaSource(
              response.answer_explanation_media_sources ?? undefined,
            )}
            onOpenChange={(openState) => {
              if (!openState) {
                clearInactivityTimer();
              }
            }}
            onContinue={handleContinue}
          />
        ));
      } catch (error) {
        const err = error as ErrorResponse | undefined;
        toast({
          type: 'error',
          title: 'Cannot submit answer',
          description: err?.data?.errors?.[0]?.detail ?? 'Please try again.',
        });
      }
    },
    [
      clearInactivityTimer,
      courseId,
      contentId,
      currentQuizId,
      currentIndex,
      currentOrder,
      handleContinue,
      isSubmittingAnswer,
      pauseAudio,
      currentQuiz?.knowledge_check_type,
      questionDescription,
      questionTitle,
      submitAnswer,
      toast,
      totalQuestionCount,
      captureAnalyticsEvent,
    ],
  );

  useEffect(() => {
    if (
      !playerState.isEnded ||
      !currentQuizId ||
      hasDescriptionModalRef.current
    ) {
      clearInactivityTimer();
      return;
    }

    clearInactivityTimer();
    inactivityTimerRef.current = setTimeout(() => {
      void handleAnswer(null);
    }, ANSWER_AUTO_SUBMIT_DELAY_MS);

    return clearInactivityTimer;
  }, [clearInactivityTimer, currentQuizId, handleAnswer, playerState.isEnded]);

  const [
    countDownAnswerAutoSubmitSeconds,
    setCountDownAnswerAutoSubmitSeconds,
  ] = useState(0);

  useEffect(() => {
    if (!playerState.isEnded) {
      setCountDownAnswerAutoSubmitSeconds(0);
      return;
    }

    setCountDownAnswerAutoSubmitSeconds(AUTO_SUBMIT_DELAY_SECONDS);
    const interval = setInterval(() => {
      setCountDownAnswerAutoSubmitSeconds((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }

        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [playerState.isEnded]);

  if (!courseId || !contentId) {
    return <MissingParameters />;
  }

  if (isLoadingQuizzes) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-slate-100 px-4 text-slate-700">
        <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-6 py-4 shadow-[0_20px_50px_rgba(15,23,42,0.15)]">
          <Loader2 className="h-5 w-5 animate-spin text-slate-500" />
          <span className="text-sm font-medium">Preparing the quiz...</span>
        </div>
      </main>
    );
  }

  const isBusy = isSubmittingAnswer;
  const progressPercent = Number.isFinite(playerState.progressPercent)
    ? Math.min(Math.max(playerState.progressPercent, 0), 100)
    : 0;

  return (
    <main className="flex h-dvh justify-center bg-slate-950 px-3 pt-3 pb-24">
      <Reel
        data={reelData}
        index={currentIndex}
        onIndexChange={handleReelIndexChange}
        defaultPlaying={false}
        playing={false}
        className="w-full max-w-[420px] aspect-auto rounded-[28px] border border-white/10 bg-slate-900 h-full text-white shadow-[0_30px_80px_rgba(15,23,42,0.5)]"
      >
        <ReelContent className="">
          {() => (
            <ReelItem className="relative flex h-full flex-col overflow-y-auto bg-[radial-gradient(circle_at_top,rgba(59,130,246,0.4),transparent),linear-gradient(160deg,rgba(15,23,42,0.92),rgba(8,47,73,0.88))]">
              <div className="relative flex h-full flex-col">
                <div className="px-5 pt-5">
                  <div className="flex items-center justify-between gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-10 w-10 rounded-full border border-white/10 bg-white/10 text-white transition hover:bg-white/20"
                      onClick={() => router.history.back()}
                      aria-label="Go Back"
                    >
                      <ArrowLeft className="h-4 w-4" />
                    </Button>
                    <div className="flex items-center gap-1.5">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-10 w-10 rounded-full border border-white/10 bg-white/5 text-white transition hover:bg-white/15"
                        onClick={() => playPreviousQuiz()}
                        aria-label="Previous Quiz"
                        disabled={!previousQuizId}
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-10 w-10 rounded-full border border-white/10 bg-white/5 text-white transition hover:bg-white/15"
                        onClick={() => playNextQuiz()}
                        aria-label="Next Quiz"
                        disabled={!nextQuizId}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  <div className="mt-3 space-y-1.5">
                    <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.22em] text-white/60">
                      <span className="truncate pr-4">{courseTitle}</span>
                      <span>
                        Question {currentOrder ?? 1} / {totalQuestionCount || 1}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="px-5 mb-4">
                  <h2 className="mt-2 text-lg font-bold leading-tight text-white">
                    {questionTitle}
                  </h2>
                  <p className="mt-2 text-sm leading-relaxed text-white/80">
                    {questionDescription}
                  </p>
                </div>

                <div className="mx-5 rounded-[20px] border border-white/10 bg-white/8 p-3 backdrop-blur">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-1.5">
                      <Button
                        variant="glass"
                        size="icon"
                        className={cn('h-10 w-10 rounded-full text-white', {
                          'opacity-50': !hasSource,
                        })}
                        onClick={() =>
                          playerState.isPlaying ? pauseAudio() : playAudio()
                        }
                        disabled={!hasSource}
                        aria-label={
                          playerState.isPlaying ? 'Pause Audio' : 'Play Audio'
                        }
                      >
                        {playerState.isPlaying ? (
                          <Pause className="h-4 w-4" />
                        ) : (
                          <Play className="h-4 w-4" />
                        )}
                      </Button>
                      <Button
                        variant="glass"
                        size="icon"
                        className={cn('h-10 w-10 rounded-full text-white', {
                          'opacity-50': !hasSource,
                        })}
                        onClick={toggleMute}
                        disabled={!hasSource}
                        aria-label={
                          playerState.isMuted ? 'Unmute Audio' : 'Mute Audio'
                        }
                      >
                        {playerState.isMuted ? (
                          <VolumeX className="h-4 w-4" />
                        ) : (
                          <Volume2 className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                    <span className="text-xs font-medium uppercase tracking-[0.22em] text-white/60">
                      {hasSource ? 'Audio Playback' : 'Audio Unavailable'}
                    </span>
                  </div>
                  {hasSource ? (
                    <div className="mt-4 flex items-center gap-2 text-xs text-white/70">
                      <span>{formatTimestamp(playerState.currentTime)}</span>
                      <div className="relative h-1 flex-1 overflow-hidden rounded-full bg-white/15">
                        <div
                          className="absolute inset-y-0 left-0 rounded-full bg-white"
                          style={{ width: `${progressPercent}%` }}
                        />
                      </div>
                      <span>{formatTimestamp(playerState.duration)}</span>
                    </div>
                  ) : (
                    <p className="mt-3 text-xs text-amber-300">
                      No audio is provided for this question.
                    </p>
                  )}
                </div>

                <div className="flex flex-1 flex-col mt-4 sm:mt-20 justify-between">
                  <div className="px-5 grid gap-3 grid-cols-2 flex-1">
                    <Button
                      variant="glow"
                      size="lg"
                      className={cn(
                        'rounded-[20px] text-xl font-bold uppercase h-full min-h-[140px] sm:min-h-[200px]',
                        {
                          'pointer-events-none opacity-60': isBusy,
                        },
                      )}
                      disabled={isBusy}
                      onClick={() => {
                        handleAnswer(true);
                      }}
                    >
                      Yes
                    </Button>
                    <Button
                      variant="destructive"
                      size="lg"
                      className={cn(
                        'rounded-[20px] text-xl font-bold uppercase h-full min-h-[140px] sm:min-h-[200px]',
                        {
                          'pointer-events-none opacity-70': isBusy,
                        },
                      )}
                      disabled={isBusy}
                      onClick={() => {
                        handleAnswer(false);
                      }}
                    >
                      No
                    </Button>
                  </div>
                  <p className="mt-2 mx-5 pb-2 text-center text-[11px] text-white/40">
                    If you do not answer within {AUTO_SUBMIT_DELAY_SECONDS}{' '}
                    seconds after the audio ends, the answer explanation will
                    open automatically.{' '}
                    {playerState.isEnded && countDownAnswerAutoSubmitSeconds
                      ? countDownAnswerAutoSubmitSeconds
                      : ''}
                  </p>
                </div>
              </div>
            </ReelItem>
          )}
        </ReelContent>
      </Reel>
      <audio ref={audioRef} className="hidden" />
    </main>
  );
};